import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Step1GoogleMaps } from "./Step1GoogleMaps";
import { Step2POS } from "./Step2POS";
import { Step3Dashboard } from "./Step3Dashboard";
import { SCROLL_CONFIG, MOBILE_BREAKPOINT, VISIBILITY_BUFFER } from "./constants";

interface ScrollDrivenContainerProps {
  title?: string;
  description?: string;
  subdescription?: string;
}

export const ScrollDrivenContainer = ({
  title = "Set Up in Minutes",
  description = "Integrating Tag-Inn into your business is effortless. We automatically fetch your data and prepare a personalized dashboard — no manual entry, no complicated setup.",
  subdescription = "Just connect and you're ready to go."
}: ScrollDrivenContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Check for reduced motion preference (following useScrollParallax.ts pattern)
  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  // Responsive mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Early return for reduced motion: show all steps stacked
  if (prefersReducedMotion) {
    return (
      <div className="bg-background">
        {/* Section title for reduced motion - left aligned */}
        <div className="pt-16 sm:pt-20 pb-8 sm:pb-12 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-foreground">
              {title}
            </h2>
          </div>
          <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-relaxed">
            <span className="text-foreground">Find your business. </span>
            <span className="text-muted-foreground/50">Connect your POS. </span>
            <span className="text-muted-foreground/50">Dashboard ready.</span>
          </div>
        </div>
        <div className="space-y-16 py-8 max-w-7xl mx-auto px-6 lg:px-8">
          <Step1GoogleMaps />
          <Step2POS />
          <Step3Dashboard />
        </div>
      </div>
    );
  }

  // Get responsive config
  const config = isMobile ? SCROLL_CONFIG.MOBILE : SCROLL_CONFIG.DESKTOP;

  // Framer Motion scroll tracking
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Step 1: Visible at start, fades out
  const step1Opacity = useTransform(
    scrollYProgress,
    [0, config.STEP_1_FADE_OUT_START, config.STEP_1_FADE_OUT_END],
    [1, 1, 0]
  );
  const step1Scale = useTransform(
    scrollYProgress,
    [0, config.STEP_1_FADE_OUT_START, config.STEP_1_FADE_OUT_END],
    [1, 1, 0.95]
  );

  // Step 1 internal progress (pause at start, then 0-1 while animating, then holds at 1)
  const step1Progress = useTransform(
    scrollYProgress,
    [0, config.STEP_1_CONTENT_START, config.STEP_1_CONTENT_COMPLETE, config.STEP_1_FADE_OUT_START],
    [0, 0, 1, 1]
  );

  // Step 2: Fades in, visible, then fades out
  const step2Opacity = useTransform(
    scrollYProgress,
    [
      config.STEP_2_FADE_IN_START,
      config.STEP_2_FADE_IN_END,
      config.STEP_2_FADE_OUT_START,
      config.STEP_2_FADE_OUT_END,
    ],
    [0, 1, 1, 0]
  );
  const step2Scale = useTransform(
    scrollYProgress,
    [
      config.STEP_2_FADE_IN_START,
      config.STEP_2_FADE_IN_END,
      config.STEP_2_FADE_OUT_START,
      config.STEP_2_FADE_OUT_END,
    ],
    [0.95, 1, 1, 0.95]
  );

  // Step 2 internal progress (0-1 while step is active, then holds at 1 for pause)
  const step2Progress = useTransform(
    scrollYProgress,
    [config.STEP_2_FADE_IN_END, config.STEP_2_CONTENT_COMPLETE, config.STEP_2_FADE_OUT_START],
    [0, 1, 1]
  );

  // Step 3: Fades in and stays visible
  const step3Opacity = useTransform(
    scrollYProgress,
    [config.STEP_3_FADE_IN_START, config.STEP_3_FADE_IN_END, 1],
    [0, 1, 1]
  );
  const step3Scale = useTransform(
    scrollYProgress,
    [config.STEP_3_FADE_IN_START, config.STEP_3_FADE_IN_END, 1],
    [0.95, 1, 1]
  );

  // Step 3 internal progress (0-1 while step is active, then holds at 1 for pause)
  const step3Progress = useTransform(
    scrollYProgress,
    [config.STEP_3_FADE_IN_END, config.STEP_3_CONTENT_COMPLETE, 1],
    [0, 1, 1]
  );

  // Progressive fill background positions for step tabs (100% = empty, 0% = full)
  // Map directly from scrollYProgress to handle all states (muted -> filling -> filled)
  const step1FillPosition = useTransform(
    scrollYProgress,
    [0, config.STEP_1_CONTENT_START, config.STEP_1_CONTENT_COMPLETE, config.STEP_1_FADE_OUT_START],
    ["100% 0", "100% 0", "0% 0", "0% 0"]
  );
  const step2FillPosition = useTransform(
    scrollYProgress,
    [0, config.STEP_2_FADE_IN_END, config.STEP_2_CONTENT_COMPLETE, 1],
    ["100% 0", "100% 0", "0% 0", "0% 0"]
  );
  const step3FillPosition = useTransform(
    scrollYProgress,
    [0, config.STEP_3_FADE_IN_END, config.STEP_3_CONTENT_COMPLETE, 1],
    ["100% 0", "100% 0", "0% 0", "0% 0"]
  );

  // Current step indicator (optimized to only update when step changes)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const lastStepRef = useRef<1 | 2 | 3>(1);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      let newStep: 1 | 2 | 3;
      // Thresholds aligned with new timing in constants.ts
      // Step 1 ends ~32%, Step 2 ends ~62%, Step 3 starts ~58%
      if (latest < 0.30) {
        newStep = 1;
      } else if (latest < 0.60) {
        newStep = 2;
      } else {
        newStep = 3;
      }

      // Only update state if step actually changed
      if (newStep !== lastStepRef.current) {
        setCurrentStep(newStep);
        lastStepRef.current = newStep;
      }
    });

    return () => unsubscribe();
  }, [scrollYProgress]);

  // Conditional rendering - only mount visible/near-visible steps
  const [shouldRenderStep1, setShouldRenderStep1] = useState(true);
  const [shouldRenderStep2, setShouldRenderStep2] = useState(false);
  const [shouldRenderStep3, setShouldRenderStep3] = useState(false);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      // Step 1: Visible from start until Step 2 fade out completes
      setShouldRenderStep1(latest < config.STEP_2_FADE_OUT_END + VISIBILITY_BUFFER);

      // Step 2: Visible from before fade in starts until after fade out ends
      setShouldRenderStep2(
        latest >= config.STEP_2_FADE_IN_START - VISIBILITY_BUFFER &&
        latest < config.STEP_3_FADE_IN_END + VISIBILITY_BUFFER
      );

      // Step 3: Visible from before fade in starts until end
      setShouldRenderStep3(latest >= config.STEP_3_FADE_IN_START - VISIBILITY_BUFFER);
    });

    return () => unsubscribe();
  }, [scrollYProgress, config]);

  return (
    <div
      ref={containerRef}
      className="relative bg-background"
      style={{ height: `${config.TOTAL_HEIGHT_VH}vh` }}
      role="region"
      aria-label="Interactive setup walkthrough"
    >
      {/* Section Title - Left aligned like WhyChooseSection */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16">
        <div className="mb-8">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-foreground">
            {title}
          </h2>
          <p className="mt-6 text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-3xl leading-relaxed">
            {description}
          </p>
          {subdescription && (
            <p className="mt-3 text-base sm:text-lg text-muted-foreground/70">
              {subdescription}
            </p>
          )}
        </div>
      </div>

      {/* Sticky container - holds step tabs and content */}
      <div className="sticky top-12 h-[calc(100vh-96px)] flex flex-col bg-background">
        {/* Step Text Tabs - Large text with progressive fill */}
        <div className="max-w-7xl mx-auto w-full px-6 lg:px-8 py-4">
          <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-relaxed">
            {/* Step 1: Filling when active, solid when complete */}
            <motion.span
              style={{
                background: "linear-gradient(to right, hsl(var(--foreground)) 0%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground) / 0.5) 50%, hsl(var(--muted-foreground) / 0.5) 100%)",
                backgroundSize: "200% 100%",
                backgroundPosition: step1FillPosition,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Find your business.{' '}
            </motion.span>
            {/* Step 2: Muted until active, filling when active, solid when complete */}
            <motion.span
              style={{
                background: "linear-gradient(to right, hsl(var(--foreground)) 0%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground) / 0.5) 50%, hsl(var(--muted-foreground) / 0.5) 100%)",
                backgroundSize: "200% 100%",
                backgroundPosition: step2FillPosition,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Connect your POS.{' '}
            </motion.span>
            {/* Step 3: Muted until active, filling when active */}
            <motion.span
              style={{
                background: "linear-gradient(to right, hsl(var(--foreground)) 0%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground) / 0.5) 50%, hsl(var(--muted-foreground) / 0.5) 100%)",
                backgroundSize: "200% 100%",
                backgroundPosition: step3FillPosition,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Dashboard ready.
            </motion.span>
          </div>
        </div>

        {/* Content area - remaining height */}
        <div className="relative flex-1 flex items-center justify-center px-6 overflow-hidden">
          {/* Step 1: Google Maps - Conditionally rendered */}
          {shouldRenderStep1 && (
            <motion.div
              style={{
                opacity: step1Opacity,
                scale: step1Scale,
                pointerEvents: currentStep === 1 ? "auto" : "none",
              }}
              className="absolute inset-0 flex items-center justify-center px-6"
            >
              <Step1GoogleMaps scrollProgress={step1Progress} />
            </motion.div>
          )}

          {/* Step 2: POS - Conditionally rendered */}
          {shouldRenderStep2 && (
            <motion.div
              style={{
                opacity: step2Opacity,
                scale: step2Scale,
                pointerEvents: currentStep === 2 ? "auto" : "none",
              }}
              className="absolute inset-0 flex items-center justify-center px-6"
            >
              <Step2POS scrollProgress={step2Progress} />
            </motion.div>
          )}

          {/* Step 3: Dashboard - Conditionally rendered */}
          {shouldRenderStep3 && (
            <motion.div
              style={{
                opacity: step3Opacity,
                scale: step3Scale,
                pointerEvents: currentStep === 3 ? "auto" : "none",
              }}
              className="absolute inset-0 flex items-center justify-center px-6"
            >
              <Step3Dashboard scrollProgress={step3Progress} />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
